/**
 * PrintQueueMonitor.jsx
 *
 * Componente que:
 *  1. Escucha la cola SSE de impresión (usePrintQueue)
 *  2. Muestra un indicador discreto del estado de la impresora
 *  3. Permite enviar un ticket de prueba
 *
 * Solo se activa si el usuario habilitó "Impresora local automática"
 * en localStorage (clave: posent_autoprint_enabled).
 */

import React, { useState, useEffect } from 'react';
import {
  Tooltip,
  IconButton,
  Box,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Stack
} from '@mui/material';
import {
  Print as PrintIcon,
  PrintDisabled as PrintDisabledIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as OfflineIcon,
  Science as TestIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import usePrintQueue from '../../hooks/usePrintQueue';
import { useSnackbar } from 'notistack';

const STORAGE_KEY = 'posent_autoprint_enabled';

const PrintQueueMonitor = () => {
  const [enabled, setEnabled] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const { isListening, printerOnline, lastPrintedSale, printCount, error, testPrint } = usePrintQueue({ enabled });

  const handleToggle = (checked) => {
    setEnabled(checked);
    try {
      localStorage.setItem(STORAGE_KEY, String(checked));
    } catch {}
    enqueueSnackbar(
      checked ? '🖨️ Impresora local activada — escuchando ventas nuevas' : 'Impresora automática desactivada',
      { variant: checked ? 'success' : 'info' }
    );
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const result = await testPrint();
      enqueueSnackbar(result?.message || 'Prueba enviada', { variant: 'success' });
    } catch {
      enqueueSnackbar('Error enviando prueba de impresión', { variant: 'error' });
    } finally {
      setTesting(false);
    }
  };

  const statusColor = !enabled ? 'default' : printerOnline ? 'success' : 'error';
  const statusLabel = !enabled ? 'Auto-impresión OFF' : printerOnline ? 'Impresora lista' : 'Sin conexión';
  const StatusIcon = printerOnline && enabled ? PrintIcon : PrintDisabledIcon;

  return (
    <>
      {/* Indicador compacto en el header */}
      <Tooltip title={statusLabel}>
        <IconButton
          size="small"
          onClick={() => setSettingsOpen(true)}
          sx={{
            color: !enabled ? 'text.disabled' : printerOnline ? 'success.main' : 'warning.main',
            position: 'relative'
          }}
          id="print-queue-monitor-btn"
        >
          <StatusIcon fontSize="small" />
          {enabled && printerOnline && (
            <Box
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'success.main',
                border: '1px solid white',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.4 }
                }
              }}
            />
          )}
        </IconButton>
      </Tooltip>

      {/* Modal de configuración */}
      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth="xs"
        fullWidth
        id="print-queue-settings-dialog"
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PrintIcon color={enabled && printerOnline ? 'success' : 'action'} />
          Impresora Local Automática
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="info" variant="outlined" sx={{ fontSize: '0.82rem' }}>
              Cuando activás esto en esta PC, cada venta que se haga — desde el celular, tablet o aquí —
              se imprimirá automáticamente en la impresora conectada a esta computadora.
            </Alert>

            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  onChange={(e) => handleToggle(e.target.checked)}
                  color="success"
                  id="autoprint-toggle"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Activar impresión automática
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Esta PC actuará como impresora central
                  </Typography>
                </Box>
              }
            />

            <Divider />

            {/* Estado actual */}
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                Estado
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
                <Chip
                  icon={enabled && printerOnline ? <CheckCircleIcon /> : <OfflineIcon />}
                  label={statusLabel}
                  color={statusColor}
                  size="small"
                  variant={enabled ? 'filled' : 'outlined'}
                />
                {enabled && !printerOnline && (
                  <Typography variant="caption" color="error.main">
                    {error || 'Conectando...'}
                  </Typography>
                )}
              </Stack>
              {printCount > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  ✅ {printCount} ticket(s) impresos en esta sesión
                </Typography>
              )}
              {lastPrintedSale && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Último: {lastPrintedSale.numero_venta} — ${lastPrintedSale.total}
                </Typography>
              )}
            </Box>

            {enabled && (
              <>
                <Divider />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                    Prueba
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1, fontSize: '0.8rem' }}>
                    Enviá un ticket de prueba para verificar que la impresora responde.
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={testing ? <CircularProgress size={14} /> : <TestIcon />}
                    onClick={handleTest}
                    disabled={testing || !printerOnline}
                    id="print-queue-test-btn"
                  >
                    {testing ? 'Enviando...' : 'Imprimir ticket de prueba'}
                  </Button>
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setSettingsOpen(false)} id="print-queue-close-btn">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PrintQueueMonitor;
